// One marker per level (DA-5042, the hiring roleplay rebuild): each level above
// Weak names ONE observable thing a person at that level does, a step up from
// the level below. Elliott grades a response by which markers it meets, so a
// level that holds two things can't be graded.
//
// The same check as the backend's (shared/services/capabilitySetup/levelRule.ts
// there), shown under each level as the admin types. The backend is the one
// that refuses: confirming levels that break the rule answers 400, except
// levels confirmed before the rule, which stand until they change.

import type { CapabilityLevel } from '../Program/components/learning-lifecycle/teamCapability';
import { LEVEL_ORDER } from '../Program/components/learning-lifecycle/teamCapability';
import type { LevelCriteria } from './capabilitySetup';

export const MARKER_LEVELS: CapabilityLevel[] = ['developing', 'capable', 'advanced'];
export const MAX_MARKER_WORDS = 30;

const TITLE: Record<CapabilityLevel, string> = { weak: 'Weak', developing: 'Developing', capable: 'Capable', advanced: 'Advanced' };

/** Words that follow a verb, not a plural noun: "and checks their", ", asks a". */
const OBJECT =
  '(?:the|a|an|their|them|they|it|its|up|back|on|out|in|what|why|how|when|whether|which|that|this|to|with|for|one|any|each|every|both|all|who|where|if|someone|people|others)';
const JOINED_ACTIONS = [
  new RegExp(`(?:,|\\band\\b|\\bthen\\b)\\s+(?:also\\s+|then\\s+)?[a-z]{3,}s\\s+${OBJECT}\\b`, 'i'),
  /,\s+(?:and|then)\s+/i,
  /\b(?:as well as|and also|and then|before also)\b/i
];
const REFERS_TO_LEVEL =
  /\b(?:all of (?:that|the above|this)|everything (?:above|below|at|in)|the (?:level|one) (?:below|above)|previous level|(?:weak|developing|capable|advanced) level)\b/i;

export interface LevelProblem {
  level: CapabilityLevel;
  problem: string;
}

const norm = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const wordCount = (text: string) => (text.trim().match(/\S+/g) || []).length;
const sentences = (text: string) =>
  text
    .trim()
    .split(/(?<=[.!?])\s+(?=["'(]?[A-Z])/)
    .filter((s) => s.trim());

/** What breaks the rule in one capability's levels, level by level. Weak is the floor and is never checked. */
export function levelProblems(criteria: LevelCriteria): LevelProblem[] {
  const out: LevelProblem[] = [];
  const text = (l: CapabilityLevel) => String(criteria[l] ?? '').trim();
  for (const level of MARKER_LEVELS) {
    const value = text(level);
    if (!value) continue;
    const add = (problem: string) => out.push({ level, problem });
    const same = LEVEL_ORDER.slice(0, LEVEL_ORDER.indexOf(level)).find((below) => text(below) && norm(text(below)) === norm(value));
    if (same) add(`The same as ${TITLE[same]}. Each level is a step up from the ones below.`);
    else if (/[;\n•]/.test(value)) add('A list. Name one observable thing.');
    else if (sentences(value).length > 1) add('More than one sentence. Name one observable thing.');
    else if (REFERS_TO_LEVEL.test(value)) add('Leans on another level. Name only the one thing this level adds.');
    else if (JOINED_ACTIONS.some((re) => re.test(value))) add('Holds more than one thing. Keep one, and move the other up or drop it.');
    else if (wordCount(value) > MAX_MARKER_WORDS) add(`Long for one thing (${wordCount(value)} words, at most ${MAX_MARKER_WORDS}).`);
  }
  return out;
}
